import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { setInvitationsSearch } from 'src/app/actions/invitations-search.actions';
import { Candidate } from 'src/app/models/candidate.model';
import { Invitation, InvitationSearch } from 'src/app/models/invitation.model';
import { Jobtype } from 'src/app/models/jobtype.model';
import { Company } from 'src/app/models/office.model';
import { Pagination } from 'src/app/models/pagination.model';
import { Process } from 'src/app/models/process.model';
import { TestModel } from 'src/app/models/test-model.model';
import { fromCompanies } from 'src/app/reducers/companies.reducer';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { fromJobtypes } from 'src/app/reducers/jobtypes.reducer';
import { fromProcesses } from 'src/app/reducers/processes.reducer';
import { fromTestModels } from 'src/app/reducers/test-models.reducer';
import { InvitationService } from 'src/app/services/invitation.service';
import { TestSearchService } from 'src/app/services/test-search.service';
import { fromInvitations } from 'src/app/reducers/invitations.reducer';
import { resetInvitations, setInvitations } from 'src/app/actions/invitations.actions';
import { Consultant } from 'src/app/models/consultant.model';
import { UtilsService } from 'src/app/services/utils.service';
import { Period } from 'src/app/models/period.model';
import { CustomValidators } from 'src/app/helpers/custom-validators';
import { AppConfig } from 'src/app/helpers/app.config';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExportReportsComponent } from '../export-reports/export-reports.component';
import { ExportScoresComponent } from '../export-scores/export-scores.component';
import { MassiveSendComponent } from '../massive-send/massive-send.component';
import { TypesPermission } from 'src/app/models/Enums';
import { ReportService } from 'src/app/services/report.service';
import { fromInvitationsSearch } from 'src/app/reducers/invitations-search.reducer';

@Component({
  selector: 'ct-test-search',
  templateUrl: './test-search.component.html',
  styleUrls: ['./test-search.component.scss']
})
export class TestSearchComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ExportReportsComponent) exportReports;
  @ViewChild(ExportScoresComponent) exportScores;
  @ViewChild(MassiveSendComponent) massiveSend;

  invitations$: Observable<Pagination<Invitation>>;
  searchInvitations$: Observable<InvitationSearch>;
  consultant$: Observable<Consultant>;
  jobtypes$: Observable<Jobtype[]>;

  showActions = true;
  canExportScores = false;

  testModels: TestModel[] = [];
  companies: Company[] = [];
  jobtypes: Jobtype[];
  processes: Process[];
  consultant: Consultant;
  HCC: number[];

  jobtype: Jobtype = null;

  periods: Period[] = [
    { value: '0', key: 'FORTNIGHT' },
    { value: '1', key: 'ONEMONTH' },
    { value: '3', key: 'THREEMONTHS' },
    { value: '6', key: 'SIXMONTHS' },
    { value: '12', key: 'ONEYEAR' }
  ];

  consultantSubscription: Subscription;

  invitationSearchForm: FormGroup = this.fb.group({
    randstadCandidate: ['', [Validators.minLength(3), CustomValidators.validCandidateIdCharacters]],
    email: ['', Validators.email],
    idCard: ['', [CustomValidators.validCharacters]],
    name: ['', [Validators.minLength(3), CustomValidators.requiredWith('surname'), CustomValidators.validCharacters]],
    surname: ['', [Validators.minLength(3), CustomValidators.requiredWith('name'), CustomValidators.validCharacters]],
    invitationId: ['', CustomValidators.validCharacters],
    testId: ['', CustomValidators.validCharacters],
    process: [[]],
    status: [''],
    jobtype: [null],
    testModel: [[]],
    createdBy: ['office'],
    period: ['0']
  });

  statusListHide = true;
  allUnselect = false;
  allSelect = false;
  itemStatus = [
    {
      status: 'pending',
      selected: true,
      color: 'orange',
      value: 'pending'
    },
    {
      status: 'active',
      selected: true,
      color: 'green',
      value: 'active'
    },
    {
      status: 'completed',
      selected: true,
      color: 'blue',
      value: 'completed'
    },
    {
      status: 'expired',
      selected: true,
      color: 'red',
      value: 'expired'
    }
  ];

  viewInit = false;

  constructor(
    private store: Store,
    private ngZone: NgZone,
    private fb: FormBuilder,
    private invitationService: InvitationService,
    private testSearchService: TestSearchService,
    private router: Router,
    private modal: NgbModal,
    private translateService: TranslateService,
    private reportService: ReportService,
    public utilsService: UtilsService,
  ) { }

  ngOnInit(): void {
    this.store.dispatch(resetInvitations());
    this.invitations$ = this.store.pipe(select(fromInvitations.selectInvitationsState));
    this.HCC = AppConfig.settings.OFFICES_WITH_PROJECTS.split(',').map(officeId => Number(officeId));

    this.searchInvitations$ = this.store.pipe(select(fromInvitationsSearch.selectInvitationsSearchState));

    this.searchInvitations$.subscribe(invitationSearch => {
      if (invitationSearch) {
        this.invitationSearchForm.setValue({
          randstadCandidate: invitationSearch.randstadCandidate,
          email: invitationSearch.email,
          idCard: invitationSearch.idCard,
          name: invitationSearch.name,
          surname: invitationSearch.surname,
          invitationId: invitationSearch.invitationId,
          testId: invitationSearch.testId,
          process: invitationSearch.process,
          status: invitationSearch.status,
          jobtype: invitationSearch.jobtype,
          testModel: invitationSearch.testModel,
          createdBy: invitationSearch.createdBy,
          period: invitationSearch.period,
        });
        if (invitationSearch.status.length) {
          this.itemStatus.map(itemstatus => itemstatus.selected = invitationSearch.status.includes(itemstatus.status));
        }
      }
    });

    this.store.pipe(select(fromTestModels.selectTestModelsAlphabetically)).subscribe(testModels => {
      // Task (B) #236421
      if (this.testModels.length === 0) {
        this.testModels = testModels;
      }
    });

    this.store.pipe(select(fromCompanies.selectCompaniesState)).subscribe(companies => {
      this.companies = companies;
    });

    this.store.pipe(select(fromProcesses.selectProcessesState)).subscribe(processes => {
      this.processes = processes.data;
    });

    this.store.pipe(select(fromJobtypes.selectBusinessJobtypes)).subscribe(jobtypes => {
      this.jobtypes = jobtypes;
    });

    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));

    this.consultantSubscription = this.consultant$.subscribe(consultant => {
      this.consultant = consultant;
      // al cambiar de oficina (cambia el estado del consultor) se relanza la busqueda y no nada mas llegar a la pantalla
      if (localStorage.getItem('lastUrl') === '/consultant/tests') {
        this.onSubmit();
      }
      // Varificamos los permisos del consultor
      this.checkConsultantPermissions(consultant);

      // añadimos la validación del campo de documento seún el país
      const idCardInput = this.invitationSearchForm.controls.idCard;

      if (consultant.client.id == 1) {
        idCardInput.setValidators([CustomValidators.documentOrPassportValidator, CustomValidators.validCharacters]);
      } else {
        idCardInput.setValidators([CustomValidators.validCharacters]);
      }
    });

    this.invitationSearchForm.get('name').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.invitationSearchForm.get('surname').updateValueAndValidity();
      this.invitationSearchForm.get('surname').markAsTouched();
    });
    this.invitationSearchForm.get('surname').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.invitationSearchForm.get('name').updateValueAndValidity();
      this.invitationSearchForm.get('name').markAsTouched();
    });
  }

  ngAfterViewInit(): void {
    localStorage.setItem('lastUrl', this.router.url);
    setTimeout(() => { this.viewInit = true; }, 500);
  }

  deleteFilters() {
    this.itemStatus.map(itemstatus => itemstatus.selected = true);
    this.invitationSearchForm.reset();
    this.invitationSearchForm.get('createdBy').setValue('office');
    this.invitationSearchForm.get('period').setValue('0');
  }

  onSubmit() {
    this.store.pipe(select(fromConsultant.selectConsultantState), take(1)).subscribe(consultant => {
      if (this.invitationSearchForm.controls.createdBy.value === 'office') {
        this.invitationSearchForm.value.consultantUniqueName = null;
        this.invitationSearchForm.value.officeId = consultant.office.id;
      } else if (this.invitationSearchForm.controls.createdBy.value === 'consultant') {
        const uniqueName = (consultant.raas.uniqueName === undefined) ? consultant.raas['unique_name'] : consultant.raas.uniqueName;
        this.invitationSearchForm.value.consultantUniqueName = uniqueName;
        this.invitationSearchForm.value.officeId = null;
      }

      if (this.invitationSearchForm.value.testModel.includes(undefined)) {
        this.invitationSearchForm.value.testModel = [];
      }

      if (this.invitationSearchForm.value.process.includes(undefined)) {
        this.invitationSearchForm.value.process = this.processes.map(process => process.id);
      }

      this.store.dispatch(setInvitationsSearch(this.invitationSearchForm.value));
      this.searchInvitations(1);
    });
  }

  searchInvitations(page: number) {
    this.ngZone.run(() => {
      this.testSearchService.search(page, this.consultant.office.id, this.consultant.client.id).pipe(take(1)).subscribe(
        invitations => this.store.dispatch(setInvitations(invitations)),
        error => this.utilsService.toast('error', 'CannotGetTests', true, this.utilsService.getNacsData({endpoint: 'GET' + AppConfig.settings.API + ' /tests', body: {page: page, consultantOfficeId: this.consultant.office.id}, error: {errorType: error.errorType, errorCode: error.errorCode, message: error.message}}))
      );
    });
  }

  openHistory(candidate: Candidate) {
    this.router.navigate(['consultant/candidates/history/' + candidate.id]);
  }

  sendReminder(invitation: Invitation) {
    this.store.pipe(take(1), select(fromConsultant.selectConsultantState)).subscribe(consultant => {
      this.invitationService.sendReminder({
        id: [invitation.id]
      }).subscribe(result => {
        this.utilsService.toast('success', 'ReminderSent');
        // this.ngOnInit();
      }, error => {
        this.utilsService.toast('error', 'CannotSendReminder');
      });
    });
  }

  toogleStatusList(status) {
    status === 'close'
      ? this.statusListHide = true
      : this.statusListHide = !this.statusListHide;
  }

  statusSelected(item) {
    this.allUnselect = true;
    this.allSelect = true;
    item.selected = !item.selected;
    const statusForm = [];
    this.itemStatus.map(status => {
      if (status.selected) {
        statusForm.push(status.status.toLowerCase()),
          this.allUnselect = false;
      } else {
        this.allSelect = false;
      }
    });
    this.allUnselect || this.allSelect
      ? this.invitationSearchForm.get('status').setValue('')
      : this.invitationSearchForm.get('status').setValue(statusForm);
  }

  // Si es un periodo de muchos meses, advertimos al usuario de que la búsqueda es posible que falle.
  checkPeriod(item) {
    if (Number(item.invitationSearchForm.controls.period.value) > 6 || item.invitationSearchForm.controls.period.value === null) {
      this.utilsService.toast('warning', 'SearchPeriodTooLong');
      console.warn('El periodo de búsqueda es muy amplio. Te recomendamos reducirlo para evitar incidencias.');
    }
  }

  ngOnDestroy(): void {
    this.consultantSubscription.unsubscribe();
  }

  // Monta el enlace de la home del candidato de los test
  async copyCandidateHomeLink(token: string) {
    let urlToken = '';
    try {
      if (token !== undefined) {
        this.utilsService.toast('success', 'INVITESCOPY');
        urlToken = this.utilsService.getHomeLink(token);
        if (navigator.clipboard !== undefined) {
          await navigator.clipboard.writeText(urlToken);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  groupByFn = (item) => this.translateService.instant('COMMON.ALL');

  // Comprobamos los permisos del consultor para utilizar el botón de Exportar Puntuación
  checkConsultantPermissions(consultant: Consultant) {
    // comprobamos que existe contenido en los permisos
    if (consultant.permissions?.length === 1) {
      // buscamos dentro de los nodes (los permisos), el id que necesitamos y evaluamos la propiedad
      if (consultant.permissions[0].nodes.find(node => node.id === TypesPermission.RTAEXPORTSCORES).resources[0].allowChleck === true) {
        this.canExportScores = true;
      }
    }
  }

  exportModal(option: string) {
    let canContinue = false;

    this.invitations$.pipe(take(1)).subscribe(async invitations => {
      if (option === 'massiveSend') {
        canContinue = true;
        if (invitations.total > 1000) {
          this.utilsService.toast('warning', 'TOOMANYINVITATIONSTOSEND');
          canContinue = false;
        }

        // No permitir envio masivo si la busqueda contiene el proceso general
        await this.searchInvitations$.pipe(take(1)).subscribe(invitationSearch => {
          if (
            invitationSearch?.process.some(process => this.processes.filter(p => p.name === 'General' && p.locked).map(p => p.id).includes(process)) ||
            invitationSearch?.process.includes(undefined) || !invitationSearch?.process.length
          ) {
            this.utilsService.toast('warning', 'GENERALPROCESSNOTALLOWED');
            canContinue = false;
          }
        });

        if (!canContinue) {
          return;
        }
        canContinue = false;
      }

      invitations?.data.forEach(invitation => invitation.tests.forEach(test => {
        // reports/scores: comprobamos si no tiene ningun test como no completado para no dejarle avanzar
        if (option !== 'massiveSend' && test.status === 'completed') { canContinue = true; }
        // massiveSend: comprobamos si no tiene ningun test como pendiente o caducado para no dejarle avanzar
        if (option === 'massiveSend' && (test.status === 'pending' || test.status === 'expired')) { canContinue = true; }
      }));

      if (!canContinue) {
        if (option !== 'massiveSend') {
          this.utilsService.toast('warning', 'EXPORTREPORTALLTESTPENDING');
        } else {
          this.utilsService.toast('warning', 'EXPORTREPORTALLTESTCOMPLETED');
        }
      } else {
        switch (option) {
          case 'reports':
            this.exportReports.open();
            break;
          case 'scores':
            this.exportScores.open();
            break;
          case 'massiveSend':
            this.massiveSend.open();
            break;
        }
      }
    });
  }

  getEvalualiaReport(testId, randstadCandidate, invitationId, officeId) {
    const reportParams = {
      reportTypeId: 2,
      testId: testId,
      languageId: 1,
      origin: 'ct',
      randstadCandidate: randstadCandidate,
      invitationId: invitationId,
      officeId: officeId,
    };

    this.reportService.generateReportToken(reportParams).subscribe(res => {
      this.reportService.getEvalualiaWebReport(res.token, 1);
    }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({endpoint: 'GET' + AppConfig.settings.API + 'report/token', body: reportParams, error: {errorType: error.errorType, errorCode: error.errorCode, message: error.message}})));
  }

}
